import { Component, DoCheck, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ChartCommentTextPosition, ChartCommentV2, PlanV2, WeeklyReportV2 } from 'src/app/core/models';
import {
  WeeklyReportsRegenerationRequest,
  WeeklyReportsRegenerationResponse,
  WeeklyReportsV2Service,
} from 'src/app/core/services/weekly-reports.v2.service';
import { ChartCommentsV2Service } from 'src/app/core/services/chart-comments.v2.service';
import Swal from 'sweetalert2';
import { CustomLineChartComponent, ChartSection, ChartDataPoint, ChartConfig, ChartAnnotation } from '../custom-line-chart/custom-line-chart.component';
import { ComunicationService } from 'src/app/pages/medical/bip/services/comunication.service';
import { loadingModal, modalClose, modalError, modalSuccess, modalWarning } from '../../utils/modal';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { take } from 'rxjs';
import { formatDateFromYmd, formatDateToYmd } from '../../utils/formatDate';
import {
  WeeklyReportRegenerationDialogComponent,
  WeeklyReportRegenerationDialogData,
  WeeklyReportRegenerationDialogResult,
} from './weekly-report-regeneration-dialog/weekly-report-regeneration-dialog.component';

type AnnotationBand = 'objective' | 'comment';

type PreparedAnnotation = ChartAnnotation & {
  band: AnnotationBand;
  wrappedLines: string[];
  measuredWidth: number;
  measuredHeight: number;
  estimatedMarkerX: number;
  labelLeft: number;
  labelRight: number;
};

@Component({
  selector: 'app-saturday-chart',
  templateUrl: './saturday-chart.component.html',
  styleUrls: ['./saturday-chart.component.scss']
})
export class SaturdayChartComponent implements OnInit, OnChanges, DoCheck {
  private static readonly ESTIMATED_TREATMENT_WIDTH = 720;
  private static readonly COMMENT_MAX_WIDTH = 380;
  private static readonly OBJECTIVE_MAX_WIDTH = 96;
  private static readonly ESTIMATED_CHAR_WIDTH = 8;
  private static readonly TEXT_LINE_HEIGHT = 14;
  private static readonly HORIZONTAL_COLLISION_PADDING = 12;
  private static readonly COMMENT_MARKER_OFFSET = 6;
  private static readonly MIN_LABEL_MARGIN = 4;

  dataGraphic!: WeeklyReportV2[];
  chartComments: ChartCommentV2[] = [];
  @Input() maladaptive!: PlanV2;
  @Input() bipId?: number;
  @Input() isFinalComponent = false;
  @Input() isEditable = true;
  @Input() weeklyReportsData: WeeklyReportV2[] = []; // Pre-loaded weekly reports data
  @Input() chartCommentsData: ChartCommentV2[] = []; // Pre-loaded comments data
  @Input() isPdfGeneration: boolean = false; // Flag to indicate PDF generation mode
  @Input() subtitle?: string;
  @Input() showGraphicConditions: boolean = true;

  @Output() imagesGenerated = new EventEmitter<string[]>();
  static allChartImages: string[] = [];

  // Reset the static array when a new PDF generation starts
  static resetChartImages() {
    SaturdayChartComponent.allChartImages = [];
    console.log('Reset static chart images array');
  }

  formTableGraphic = new FormGroup({
    datos: new FormArray([])
  });

  formCommentsTable = new FormGroup({
    comments: new FormArray([])
  });

  formAddComment = new FormGroup({
    plan_id: new FormControl<number | null>(null, Validators.required),
    comment_date: new FormControl<string | null>('', Validators.required),
    comment: new FormControl<string | null>('', [Validators.required, Validators.maxLength(50)]),
    text_position: new FormControl<ChartCommentTextPosition>('top', Validators.required)
  });

  formAddWeeklyReport = new FormGroup({
    plan_id: new FormControl<number | null>(null, Validators.required),
    week_start: new FormControl<string | null>('', Validators.required),
    week_end: new FormControl<string | null>('', Validators.required),
    value: new FormControl<number | null>(null, [Validators.required, Validators.min(0)]),
    total_units: new FormControl<number | null>(null, [Validators.min(0)])
  });

  @ViewChild(CustomLineChartComponent) chart!: CustomLineChartComponent;
  public showGraphic = false;
  isExporting = false;

  // Chart data for custom line chart
  section1Data: ChartSection = { data: [], label: 'Baseline', color: '#FF0000' };
  section2Data: ChartSection = { data: [], label: 'Treatment', color: '#20B2AA' };
  annotations: ChartAnnotation[] = [];
  chartConfig: ChartConfig = {};
  private lastChartSignature = '';
  currentDateFilters: { date_from?: string; date_to?: string } = {};
  pendingCommentScope: 'goal' | 'bip' | null = null;
  scope = '';
  isRegeneratingWeeklyReports = false;
  readonly textPositionOptions: { value: ChartCommentTextPosition; label: string }[] = [
    { value: 'top', label: 'Top' },
    { value: 'middle', label: 'Middle' },
    { value: 'bottom', label: 'Bottom' },
  ];

  private readonly rowRegenerationLoading = new Set<number>();

  private readonly weeklyReportV2Service = inject(WeeklyReportsV2Service);
  private readonly chartCommentsService = inject(ChartCommentsV2Service);
  private readonly comunicationService = inject(ComunicationService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);

  // Saturday filter for date picker
  saturdayFilter = (date: Date | null): boolean => {
    if (!date) return false;
    // Only allow Saturdays (day 6)
    return date.getDay() === 6;
  };

  ngOnInit(): void {
    this.getValues();
    this.comunicationService.updateData$.subscribe(dates => {
      this.getValues(dates.date_from, dates.date_to);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['maladaptive']) {
      this.formAddWeeklyReport.setValue({
        plan_id: this.maladaptive.id,
        week_start: '',
        week_end: '',
        value: null,
        total_units: null
      });

      this.formAddComment.setValue({
        plan_id: this.maladaptive.id,
        comment_date: '',
        comment: '',
        text_position: 'top'
      });

      if (!changes['maladaptive'].firstChange && this.dataGraphic?.length) {
        this.prepareChartData(true);
      }
    }
  }

  private getValues(date_from: string | null = null, date_to: string | null = null, forceRemote = false) {
    this.currentDateFilters = {
      ...(date_from ? { date_from } : {}),
      ...(date_to ? { date_to } : {}),
    };
    this.showGraphic = false;
    this.dataGraphic = [];
    this.chartComments = [];
    this.commentsArray.clear();

    const hasPreloadedWeekly = !forceRemote && Array.isArray(this.weeklyReportsData) && this.weeklyReportsData.length > 0;

    if (hasPreloadedWeekly) {
      this.processWeeklyReportsData(this.weeklyReportsData);
      this.loadComments(date_from || undefined, date_to || undefined);
      return;
    }

    const params: Record<string, unknown> = {
      plan_id: this.maladaptive.id,
      per_page: 500,
    };

    if (date_from) {
      params['date_start'] = date_from;
    }

    if (date_to) {
      params['date_end'] = date_to;
    }

    this.weeklyReportV2Service.list(params as never).subscribe({
      next: (response) => {
        this.processWeeklyReportsData(response.data);
        this.loadComments(date_from || undefined, date_to || undefined);
      },
      error: (error) => {
        console.log('error: ', error);
        this.loadComments(date_from || undefined, date_to || undefined);
      }
    });
  }

  /**
   * Process weekly reports data (from API or pre-loaded)
   */
  private loadComments(date_from?: string, date_to?: string) {
    const hasPreloaded = Array.isArray(this.chartCommentsData) && this.chartCommentsData.length > 0;

    if (hasPreloaded) {
      this.processChartCommentsData(this.chartCommentsData);
      return;
    }

    if (!this.maladaptive?.id) {
      return;
    }

    if (typeof this.maladaptive.bip_id === 'number') {
      this.chartCommentsService
        .getChartCommentsByPlanId(this.maladaptive.id, this.maladaptive.bip_id, date_from || undefined, date_to || undefined, 500)
        .subscribe({
          next: (response) => {
            this.processChartCommentsData(response.data ?? []);
          },
          error: (error) => {
            console.error('Error loading chart comments (plan+bip)', error);
            // Fallback to plan-only fetch
            this.chartCommentsService
              .getChartCommentsByPlanId(this.maladaptive.id, undefined, date_from || undefined, date_to || undefined, 500)
              .subscribe({ next: (response) => this.processChartCommentsData(response.data ?? []), error: (e) => console.error(e) });
          }
        });
    } else {
      this.chartCommentsService
        .getChartCommentsByPlanId(this.maladaptive.id, undefined, date_from || undefined, date_to || undefined, 500)
        .subscribe({
          next: (response) => {
            this.processChartCommentsData(response.data ?? []);
          },
          error: (error) => {
            console.error('Error loading chart comments', error);
          }
        });
    }
  }

  private processChartCommentsData(comments: (ChartCommentV2 | null | undefined)[]) {
    this.formAddComment.patchValue({ plan_id: this.maladaptive?.id ?? null });

    const validComments: ChartCommentV2[] = (comments || []).filter((c): c is ChartCommentV2 => !!c);

    // Warn if any invalid items were dropped (helps debug malformed API responses)
    if ((comments || []).length !== validComments.length) {
      // eslint-disable-next-line no-console
      console.warn('processChartCommentsData: dropped invalid/null comment items', { originalCount: (comments || []).length, validCount: validComments.length });
    }

    this.chartComments = [...validComments].sort((a, b) => {
      const aDate = new Date(a.comment_date as string | Date).getTime();
      const bDate = new Date(b.comment_date as string | Date).getTime();
      return aDate - bDate;
    });

    this.commentsArray.clear();

    this.chartComments.forEach((comment) => {
      const scope = comment.bip_id !== null ? 'global' : 'goal';
      this.commentsArray.push(
        new FormGroup({
          id: new FormControl(comment.id),
          plan_id: new FormControl(comment.plan_id),
          bip_id: new FormControl(comment.bip_id ?? null),
          scope: new FormControl(scope),
          comment_date: new FormControl(this.normalizeDateValue(comment.comment_date), Validators.required),
          comment: new FormControl(comment.comment, [Validators.required, Validators.maxLength(50)]),
          text_position: new FormControl(this.normalizeCommentTextPosition(comment.text_position), Validators.required),
        })
      );
    });

    this.prepareChartData(true);
  }

  private processWeeklyReportsData(data: WeeklyReportV2[]) {
    this.dataGraphic = data;
    this.datosArray.clear();

    this.dataGraphic.forEach(element => {
      this.datosArray.push(
        new FormGroup({
          week_end: new FormControl({ value: formatDateFromYmd(element.week_end), disabled: true }),
          value: new FormControl(element.value, [Validators.required, Validators.min(0)]),
          total_units: new FormControl(element.total_units ?? null, [Validators.min(0)]),
          week_start: new FormControl(formatDateToYmd(element.week_start) ?? ''),
          plan_id: new FormControl(element.plan_id),
          id: new FormControl(element.id)
        })
      );
    });

    // Prepare chart data
    this.prepareChartData(true);
    this.showGraphic = true;

    // Only close Swal if this is the final component AND we're not in PDF generation mode
    if (this.isFinalComponent && !this.isPdfGeneration) {
      setTimeout(() => {
        Swal.close();
      }, 1000);
    }
  }

  private prepareChartData(force = false) {
    if (!this.maladaptive) {
      return;
    }

    const signature = this.computeChartSignature();
    if (!force && signature === this.lastChartSignature) {
      return;
    }
    this.lastChartSignature = signature;

    // Prepare baseline data (single point)
    if (this.maladaptive.baseline_date && this.maladaptive.baseline_level !== null && this.maladaptive.baseline_level !== undefined) {
      const baselineDate = this.createSafeDate(this.maladaptive.baseline_date);
      this.section1Data = {
        data: [{
          date: baselineDate,
          value: this.maladaptive.baseline_level
        }],
        label: 'Baseline',
        color: '#FF0000'
      };
    } else {
      this.section1Data = {
        data: [],
        label: 'Baseline',
        color: '#FF0000'
      };
    }

    const isMaladaptive = this.maladaptive.category === 'maladaptive';
    const isPerHour = isMaladaptive && this.maladaptive.timeframe === 'per_hour';

    // First, create data points from existing reports
    const existingDataPoints: ChartDataPoint[] = (this.dataGraphic || [])
      .map((report) => {
        const safeDate = this.createSafeDate(report.week_end as Date | string);
        if (!safeDate || isNaN(safeDate.getTime())) {
          return null;
        }

        const incidents = report.value ?? 0;

        if (!isPerHour) {
          return {
            date: safeDate,
            value: incidents,
          } as ChartDataPoint;
        }

        const totalUnits = report.total_units;
        const effectiveUnits = totalUnits && totalUnits > 0 ? totalUnits : 80;
        const hours = effectiveUnits / 4;
        const perHourValue = hours > 0 ? Number((incidents / hours).toFixed(1)) : null;

        return {
          date: safeDate,
          value: perHourValue,
        } as ChartDataPoint;
      })
      .filter((point): point is ChartDataPoint => point !== null);

    // Sort existing data by date (oldest first)
    existingDataPoints.sort((a, b) => {
      const aDate = this.createSafeDate(a.date as Date | string);
      const bDate = this.createSafeDate(b.date as Date | string);
      return aDate.getTime() - bDate.getTime();
    });

    // Generate treatment data with optional null-filled weeks depending on category
    let treatmentData: ChartDataPoint[] = [];
    const fillMissingWeeks = this.shouldFillMissingWeeks();

    if (existingDataPoints.length > 0) {
      if (fillMissingWeeks) {
        // Get the date range
        const firstDate = this.createSafeDate(existingDataPoints[0].date);
        const lastDate = this.createSafeDate(existingDataPoints[existingDataPoints.length - 1].date);

        // Generate all Saturdays in the range
        const allSaturdays = this.generateSaturdaySequence(firstDate, lastDate);

        // Create a map of existing data points by date key for quick lookup
        const dataMap = new Map<string, number | null>();
        existingDataPoints.forEach(point => {
          const dateKey = this.normalizeDateValue(point.date);
          dataMap.set(dateKey, point.value);
        });

        // Build complete data array with nulls for missing weeks
        treatmentData = allSaturdays.map(saturday => {
          const dateKey = this.normalizeDateValue(saturday);
          const value = dataMap.get(dateKey) ?? null;

          return {
            date: saturday,
            value: value
          } as ChartDataPoint;
        });
      } else {
        // Only include weeks that have actual data so the line stays connected
        treatmentData = existingDataPoints.map(point => ({
          date: this.createSafeDate(point.date),
          value: point.value
        }));
      }
    }

    this.section2Data = {
      data: treatmentData,
      label: 'Treatment',
      color: '#20B2AA'
    };

    // Set up chart configuration based on plan category
    const unitType = isMaladaptive ? this.maladaptive.unit_type || 'occurrences' : null;

    let yAxisLabel = '% of Trials';

    if (isMaladaptive) {
      if (unitType === 'occurrences') {
        yAxisLabel = isPerHour ? 'Incidents per Hour' : 'Frequency';
      } else {
        const unitLabel = unitType === 'seconds' ? 'Seconds' : 'Minutes';
        yAxisLabel = isPerHour ? `Duration (${unitLabel}) per Hour` : `Duration (${unitLabel})`;
      }
    }

    const chartConfig: ChartConfig = {
      lineColor: '#20B2AA',
      dotColor: '#20B2AA',
      yAxisLabel,
      xAxisLabel: 'Week',
      showValuesOnDots: true,
      useValuePills: false,
      dividerColor: '#FF0000',
      dividerLabel: 'Baseline | Treatment',
      yAxisMin: 0,
    };

    if (!isMaladaptive) {
      chartConfig.yAxisMax = 100;
    }

    if (isPerHour) {
      chartConfig.valuePrecision = 1;
      chartConfig.useDecimalTicks = true;
    }

    const treatmentDates = treatmentData
      .map((d) => this.createSafeDate(d.date as Date | string))
      .filter((date) => !isNaN(date.getTime()));

    let minTreatmentDate =
      treatmentDates.length > 0 ? new Date(Math.min(...treatmentDates.map((d) => d.getTime()))) : null;
    let maxTreatmentDate =
      treatmentDates.length > 0 ? new Date(Math.max(...treatmentDates.map((d) => d.getTime()))) : null;

    this.chartConfig = chartConfig;

    // Add annotations for objectives if available
    if ((!minTreatmentDate || !maxTreatmentDate) && this.chartComments.length > 0) {
      const commentDates = this.chartComments
        .map((comment) => this.createSafeDate(comment.comment_date as Date | string))
        .filter((date) => !!date && !isNaN(date.getTime()));

      if (commentDates.length > 0) {
        const commentMin = new Date(Math.min(...commentDates.map((d) => d.getTime())));
        const commentMax = new Date(Math.max(...commentDates.map((d) => d.getTime())));

        if (!minTreatmentDate || commentMin < minTreatmentDate) {
          minTreatmentDate = commentMin;
        }
        if (!maxTreatmentDate || commentMax > maxTreatmentDate) {
          maxTreatmentDate = commentMax;
        }
      }
    }

    this.annotations = this.buildAnnotations(minTreatmentDate, maxTreatmentDate);
  }

  private buildAnnotations(minTreatmentDate: Date | null, maxTreatmentDate: Date | null): ChartAnnotation[] {
    if (!minTreatmentDate || !maxTreatmentDate) {
      return [];
    }

    const objectiveAnnotations: PreparedAnnotation[] = [];
    const commentAnnotations: PreparedAnnotation[] = [];

    if (this.maladaptive.objectives && this.maladaptive.objectives.length > 0) {
      this.maladaptive.objectives.forEach((objective, index) => {
        if (!objective.end_date) {
          return;
        }

        const objectiveDate = this.createSafeDate(objective.end_date);
        if (objectiveDate < minTreatmentDate || objectiveDate > maxTreatmentDate) {
          return;
        }

        const text = `${objective.type}#${index + 1}`;
        objectiveAnnotations.push(
          this.createPreparedAnnotation({
            date: objectiveDate,
            text,
            color: '#9C27B0',
            type: 'line',
            textPosition: 'top',
            band: 'objective',
            align: 'center',
            wrappedLines: [text],
            isComment: false,
          })
        );
      });
    }

    if (this.chartComments && this.chartComments.length > 0) {
      this.chartComments.forEach((comment) => {
        const commentDate = this.createSafeDate(comment.comment_date as Date | string);
        if (!commentDate || isNaN(commentDate.getTime())) {
          return;
        }

        if (commentDate < minTreatmentDate || commentDate > maxTreatmentDate) {
          return;
        }

        commentAnnotations.push(
          this.createPreparedAnnotation({
            date: commentDate,
            text: comment.comment || '',
            color: '#000000',
            type: 'line',
            textPosition: this.normalizeCommentTextPosition(comment.text_position),
            band: 'comment',
            align: 'right',
            wrappedLines: comment.comment ? [comment.comment] : [],
            isComment: true,
            offsetX: SaturdayChartComponent.COMMENT_MARKER_OFFSET,
          })
        );
      });
    }

    const filteredObjectiveAnnotations = objectiveAnnotations.filter((annotation) => annotation.wrappedLines.length > 0);
    const filteredCommentAnnotations = commentAnnotations.filter((annotation) => annotation.wrappedLines.length > 0);

    this.assignAnnotationLanes(filteredObjectiveAnnotations, minTreatmentDate, maxTreatmentDate);
    this.assignCommentAnnotationLanes(filteredCommentAnnotations, minTreatmentDate, maxTreatmentDate);

    return [...filteredObjectiveAnnotations, ...filteredCommentAnnotations];
  }

  private createPreparedAnnotation(annotation: ChartAnnotation & {
    band: AnnotationBand;
    wrappedLines: string[];
    align: 'left' | 'right' | 'center';
  }): PreparedAnnotation {
    const measuredWidth = this.estimateAnnotationWidth(annotation.wrappedLines, annotation.band);
    const measuredHeight = annotation.wrappedLines.length * SaturdayChartComponent.TEXT_LINE_HEIGHT;

    return {
      ...annotation,
      measuredWidth,
      measuredHeight,
      estimatedMarkerX: 0,
      labelLeft: 0,
      labelRight: 0,
      laneIndex: 0,
    };
  }

  private assignAnnotationLanes(
    annotations: PreparedAnnotation[],
    minTreatmentDate: Date,
    maxTreatmentDate: Date
  ): void {
    if (!annotations.length) {
      return;
    }

    if (annotations[0].band === 'objective') {
      this.assignObjectiveAnnotationOffsets(annotations, minTreatmentDate, maxTreatmentDate);
      return;
    }

    const laneRightEdges: number[] = [];

    annotations
      .sort((a, b) => this.createSafeDate(a.date).getTime() - this.createSafeDate(b.date).getTime())
      .forEach((annotation) => {
        const placement = this.calculateAnnotationPlacement(annotation, minTreatmentDate, maxTreatmentDate);
        annotation.align = placement.align;
        annotation.estimatedMarkerX = placement.markerX;
        annotation.labelLeft = placement.labelLeft;
        annotation.labelRight = placement.labelRight;

        let laneIndex = 0;
        while (
          laneIndex < laneRightEdges.length &&
          annotation.labelLeft < laneRightEdges[laneIndex] + SaturdayChartComponent.HORIZONTAL_COLLISION_PADDING
        ) {
          laneIndex += 1;
        }

        annotation.laneIndex = laneIndex;
        laneRightEdges[laneIndex] = annotation.labelRight;
      });
  }

  private assignCommentAnnotationLanes(
    annotations: PreparedAnnotation[],
    minTreatmentDate: Date,
    maxTreatmentDate: Date
  ): void {
    this.textPositionOptions.forEach((option) => {
      this.assignAnnotationLanes(
        annotations.filter(
          (annotation) => this.normalizeCommentTextPosition(annotation.textPosition) === option.value
        ),
        minTreatmentDate,
        maxTreatmentDate
      );
    });
  }

  private assignObjectiveAnnotationOffsets(
    annotations: PreparedAnnotation[],
    minTreatmentDate: Date,
    maxTreatmentDate: Date
  ): void {
    const maxLabelRight =
      SaturdayChartComponent.ESTIMATED_TREATMENT_WIDTH - SaturdayChartComponent.MIN_LABEL_MARGIN;
    let previousRightEdge = -Infinity;

    annotations
      .sort((a, b) => this.createSafeDate(a.date).getTime() - this.createSafeDate(b.date).getTime())
      .forEach((annotation) => {
        const placement = this.calculateAnnotationPlacement(annotation, minTreatmentDate, maxTreatmentDate);
        let labelLeft = placement.labelLeft;
        let labelRight = placement.labelRight;
        let markerCenter = placement.markerX;

        if (labelLeft < previousRightEdge + SaturdayChartComponent.HORIZONTAL_COLLISION_PADDING) {
          const shift =
            previousRightEdge + SaturdayChartComponent.HORIZONTAL_COLLISION_PADDING - labelLeft;
          labelLeft += shift;
          labelRight += shift;
          markerCenter += shift;
        }

        if (labelRight > maxLabelRight) {
          const overflow = labelRight - maxLabelRight;
          labelLeft -= overflow;
          labelRight -= overflow;
          markerCenter -= overflow;
        }

        annotation.align = 'center';
        annotation.laneIndex = 0;
        annotation.labelLeft = labelLeft;
        annotation.labelRight = labelRight;
        annotation.estimatedMarkerX = placement.markerX;
        annotation.offsetX = markerCenter - placement.markerX;

        previousRightEdge = Math.max(previousRightEdge, labelRight);
      });
  }

  private calculateAnnotationPlacement(
    annotation: PreparedAnnotation,
    minTreatmentDate: Date,
    maxTreatmentDate: Date
  ): {
    markerX: number;
    labelLeft: number;
    labelRight: number;
    align: 'left' | 'right' | 'center';
  } {
    const markerX = this.estimateAnnotationMarkerX(
      this.createSafeDate(annotation.date),
      minTreatmentDate,
      maxTreatmentDate
    );
    const maxLabelLeft =
      SaturdayChartComponent.ESTIMATED_TREATMENT_WIDTH - annotation.measuredWidth - SaturdayChartComponent.MIN_LABEL_MARGIN;
    const minLabelLeft = SaturdayChartComponent.MIN_LABEL_MARGIN;

    if (annotation.band === 'objective') {
      const centeredLeft = markerX - annotation.measuredWidth / 2;
      const labelLeft = Math.max(minLabelLeft, Math.min(centeredLeft, maxLabelLeft));

      return {
        markerX,
        labelLeft,
        labelRight: labelLeft + annotation.measuredWidth,
        align: 'center',
      };
    }

    let align: 'left' | 'right' = 'right';
    let labelLeft = markerX + (annotation.offsetX ?? SaturdayChartComponent.COMMENT_MARKER_OFFSET);

    if (labelLeft + annotation.measuredWidth > SaturdayChartComponent.ESTIMATED_TREATMENT_WIDTH - SaturdayChartComponent.MIN_LABEL_MARGIN) {
      align = 'left';
      labelLeft = markerX - (annotation.offsetX ?? SaturdayChartComponent.COMMENT_MARKER_OFFSET) - annotation.measuredWidth;
    }

    labelLeft = Math.max(minLabelLeft, Math.min(labelLeft, maxLabelLeft));

    return {
      markerX,
      labelLeft,
      labelRight: labelLeft + annotation.measuredWidth,
      align,
    };
  }

  private estimateAnnotationMarkerX(date: Date, minTreatmentDate: Date, maxTreatmentDate: Date): number {
    const minTime = minTreatmentDate.getTime();
    const maxTime = maxTreatmentDate.getTime();
    const currentTime = date.getTime();

    if (!Number.isFinite(minTime) || !Number.isFinite(maxTime) || !Number.isFinite(currentTime)) {
      return SaturdayChartComponent.ESTIMATED_TREATMENT_WIDTH / 2;
    }

    if (maxTime === minTime) {
      return SaturdayChartComponent.ESTIMATED_TREATMENT_WIDTH / 2;
    }

    const ratio = (currentTime - minTime) / (maxTime - minTime);
    const clampedRatio = Math.max(0, Math.min(1, ratio));

    return clampedRatio * SaturdayChartComponent.ESTIMATED_TREATMENT_WIDTH;
  }

  private estimateAnnotationWidth(lines: string[], band: AnnotationBand): number {
    if (!lines.length) {
      return 0;
    }

    const rawWidth = Math.max(...lines.map((line) => line.length * SaturdayChartComponent.ESTIMATED_CHAR_WIDTH));
    const maxWidth =
      band === 'comment'
        ? SaturdayChartComponent.COMMENT_MAX_WIDTH
        : SaturdayChartComponent.OBJECTIVE_MAX_WIDTH;

    return Math.min(maxWidth, Math.ceil(rawWidth));
  }

  /**
   * Determine whether the chart should fill missing weeks with null values.
   * Training categories (caregiver, RBT, teacher) should only plot existing data points.
   */
  private shouldFillMissingWeeks(): boolean {
    const category = this.maladaptive?.category ?? '';
    const sparseCategories = new Set(['caregiver_training', 'rbt_training', 'teacher_training']);
    return !sparseCategories.has(category);
  }

  private normalizeDateValue(date: Date | string | null | undefined): string {
    if (!date) {
      return '';
    }

    if (typeof date === 'string') {
      return formatDateToYmd(date) ?? '';
    }

    const safeDate = this.createSafeDate(date);
    if (!safeDate || isNaN(safeDate.getTime())) {
      return '';
    }
    const year = safeDate.getFullYear();
    const month = (safeDate.getMonth() + 1).toString().padStart(2, '0');
    const day = safeDate.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private computeChartSignature(): string {
    if (!this.maladaptive) {
      return '';
    }

    const baselinePart = `${this.normalizeDateValue(this.maladaptive.baseline_date)}|${this.maladaptive.baseline_level ?? ''}`;

    const reportsPart = (this.dataGraphic || [])
      .map(report => `${report.id ?? ''}:${this.normalizeDateValue(report.week_end)}:${report.value ?? ''}`)
      .sort()
      .join(',');

    const objectivesPart = (this.maladaptive.objectives || [])
      .filter(obj => !!obj)
        .map((obj, idx) => `${obj.id ?? ''}:${this.normalizeDateValue(obj.end_date ?? '')}:${obj.type ?? ''}:${idx}`)
      .sort()
      .join(',');

    const commentsPart = (this.chartComments || [])
      .map(comment => `${comment.id ?? ''}:${this.normalizeDateValue(comment.comment_date)}:${comment.comment ?? ''}:${comment.text_position ?? 'top'}`)
      .sort()
      .join(',');

    return `${baselinePart}||${reportsPart}||${objectivesPart}||${commentsPart}`;
  }

  ngDoCheck(): void {
    if (!this.maladaptive || !this.showGraphic) {
      return;
    }

    this.prepareChartData();
  }

  addValue() {
    if (this.formAddWeeklyReport.valid) {
      loadingModal();

      const formData = { ...this.formAddWeeklyReport.value };

      // Convert empty string to null for total_units
      if (formData.total_units === undefined) {
        formData.total_units = null;
      }

      this.weeklyReportV2Service.create(formData).subscribe(
        {
          next: (response) => {
            modalSuccess('Add Weekly Report', response.message ?? 'Weekly report added successfully');
          },
          error: (error) => {
            console.log('error: ', error);
            modalError('Error', error.error.message);
          },
          complete: () => {
            this.refreshWeeklyReports();
            }
        }
      );
    }
    else {
      modalWarning(
        'Required Fields Missing',
        'You must fill out all required fields before proceeding.'
      );
    }
  }

  onDateChange(event: { value?: string | Date | null }) {
    if (!event.value) {
      this.week_end.setValue(null);
      this.week_start.setValue(null);
      return;
    }

    const selectedDate = new Date(event.value);

    // Validate that the selected date is a Saturday
    if (selectedDate.getDay() !== 6) {
      modalWarning(
        'Invalid Date Selection',
        'Please select a Saturday for the weekly report date.'
      );
      this.week_end.setValue(null);
      this.week_start.setValue(null);
      return;
    }

    this.week_end.setValue(formatDateToYmd(selectedDate) ?? null);

    // Set week_start to 7 days before (previous Sunday)
    const weekStartDate = new Date(selectedDate);
    weekStartDate.setDate(weekStartDate.getDate() - 6); // 6 days before Saturday = Sunday
    this.week_start.setValue(formatDateToYmd(weekStartDate) ?? null);
  }

  // Converts YYYY-MM-DD → MM-DD-YYYY (for showing on frontend)
  formatDateForDisplay(dateString: string): string {
    if (!dateString) return '';
    const parts = dateString.split('-');
    if (parts.length !== 3) return dateString; // fallback
    const [year, month, day] = parts;
    return `${month}-${day}-${year}`;
  }

  // Converts MM-DD-YYYY → YYYY-MM-DD (for sending to backend)
  formatDateForBackend(dateString: string): string {
    if (!dateString) return '';
    const parts = dateString.split('-');
    if (parts.length !== 3) return dateString; // fallback
    const [month, day, year] = parts;
    return `${year}-${month}-${day}`;
  }

  updateValue(index: number) {
    const datoActualizado = this.datosArray.at(index).getRawValue();

    this.snackBar.open('Please wait while changes are saved', 'Close', {
      duration: 2000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['snackbar-progress'],
    });

    // Convert to backend format
    if (datoActualizado.week_end) {
      datoActualizado.week_end = this.formatDateForBackend(datoActualizado.week_end);
    }

    // Convert empty string to null for total_units
    if (datoActualizado.total_units === '' || datoActualizado.total_units === undefined) {
      datoActualizado.total_units = null;
    }

    this.weeklyReportV2Service.update(datoActualizado, datoActualizado.id).subscribe({
        next: (response) => {
          if (response.status === 'success') {
            this.refreshWeeklyReports();
            this.snackBar.open('Value updated successfully', 'Close', {
              duration: 2000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['snackbar-success'],
            });

            this.datosArray
              .at(index)
              .patchValue(datoActualizado, { emitEvent: false });

            // Convert back to MM-DD-YYYY for display again
            this.datosArray.at(index).patchValue(
              {
                week_end: this.formatDateForDisplay(datoActualizado.week_end),
              },
              { emitEvent: false }
            );
          }
        },
        error: (error) => {
          console.log('error: ', error);
          modalError('Error', error.error.message);
        },
      });
  }

  async addComment(): Promise<void> {
    const r = await Swal.fire({
      title: 'Save comment to',
      html:
        '<div style="text-align:left">' +
        '<p><strong>This Goal Only</strong><br/><small>Comment will appear only on this goal\'s chart</small></p>' +
        '<p><strong>All Goals</strong><br/><small>Comment will appear on all goal charts BIP for this patient</small></p>' +
        '</div>',
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: 'This Goal Only',
      denyButtonText: 'All Goals',
      cancelButtonText: 'Cancel',
      customClass: { popup: 'swal2-content-left' }
    });

    console.log(r.isConfirmed, r.isDenied);

    if (r.isConfirmed) {
      this.pendingCommentScope = 'goal';
    } else if (r.isDenied) {
      this.pendingCommentScope = 'bip';
    } else {
      this.pendingCommentScope = null;
      return;
    }

    if (this.formAddComment.invalid) {
      modalWarning('Required Fields Missing', 'You must select a date and enter a comment (max 50 characters).');
      return;
    }

    const formValue = this.formAddComment.value;
    const commentText = (formValue.comment || '').trim();
    const commentDateNormalized = this.normalizeDateValue(formValue.comment_date ?? '');

    if (!commentText) {
      modalWarning('Comment Required', 'Please enter a comment before saving.');
      return;
    }

    if (!commentDateNormalized) {
      modalWarning('Comment Date Required', 'Please select a comment date before saving.');
      return;
    }

    // Determine scope: use pendingCommentScope if set, otherwise preserve existing behavior (plan_id from form)
    this.scope = this.pendingCommentScope ?? (formValue.plan_id ? 'goal' : 'bip');
    console.log('Determined comment scope:', this.scope);

    const payload: {
      comment: string;
      comment_date: string;
      text_position: ChartCommentTextPosition;
      plan_id?: number | null;
      bip_id?: number | null;
    } = {
      comment: commentText,
      comment_date: commentDateNormalized,
      text_position: this.normalizeCommentTextPosition(formValue.text_position),
    };

    if (this.scope === 'goal') {
      payload.plan_id = formValue.plan_id;
      payload.bip_id = null;
    } else {
      payload.bip_id = this.maladaptive?.bip_id || null;
      payload.plan_id = null;
    }

    loadingModal('Add Comment');
    console.log('Payload for new comment:', payload);

    this.chartCommentsService.create(payload).subscribe({
      next: (response: { message?: string }) => {
        // If user explicitly chose scope show specific message; otherwise keep backend message fallback
        const title =
          this.pendingCommentScope === 'goal'
            ? 'Plan-specific comment added successfully'
            : this.pendingCommentScope === 'bip'
            ? 'Global comment added successfully'
            : (response?.message || 'Comment added successfully');

        modalSuccess('Add Comment', title);
        this.formAddComment.patchValue({ comment_date: '', comment: '', text_position: 'top' });
        this.formAddComment.markAsPristine();
        this.formAddComment.markAsUntouched();
        this.loadComments(this.currentDateFilters.date_from, this.currentDateFilters.date_to);
        this.pendingCommentScope = null;
      },
      error: (error) => {
        console.log('error: ', error);
        modalError('Error', error.error?.message || 'Failed to add comment.');
      }
    });
  }

  updateComment(index: number) {
    const group = this.commentsArray.at(index) as FormGroup;

    if (!group || group.invalid) {
      modalWarning('Invalid Comment', 'Please provide a valid date and comment (max 50 characters).');
      return;
    }

    const updated = group.getRawValue();
    const commentId = updated.id;

    const payload: {
      plan_id: number | null;
      bip_id: number | null;
      comment_date: string;
      comment: string;
      text_position: ChartCommentTextPosition;
    } = {
      plan_id: updated.bip_id ? null : (updated.plan_id ?? null),
      bip_id: updated.bip_id ?? null,
      comment_date: this.normalizeDateValue(updated.comment_date),
      comment: (updated.comment || '').trim(),
      text_position: this.normalizeCommentTextPosition(updated.text_position),
    };

    if (!payload.comment) {
      modalWarning('Comment Required', 'Please enter a comment before saving.');
      return;
    }

    if (!payload.comment_date) {
      modalWarning('Comment Date Required', 'Please select a comment date before saving.');
      return;
    }

    loadingModal('Update Comment');

    this.chartCommentsService.update(payload, commentId).subscribe({
      next: (response: { message?: string }) => {
        modalSuccess('Update Comment', response?.message || 'Comment updated successfully');
        group.markAsPristine();
        this.loadComments(this.currentDateFilters.date_from, this.currentDateFilters.date_to);
      },
      error: (error) => {
        console.log('error: ', error);
        modalError('Error', error.error?.message || 'Failed to update comment.');
      }
    });
  }

  async deleteComment(index: number) {
    const commentGroup = this.commentsArray.at(index) as FormGroup;
    if (!commentGroup) {
      return;
    }
    const commentData = commentGroup.getRawValue();

    Swal.fire({
      title: 'Are you sure you want to delete this comment?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        loadingModal('Delete Comment');

        this.chartCommentsService.delete(commentData.id).subscribe({
          next: (response: { message?: string }) => {
            modalSuccess('Delete Comment', response?.message || 'Comment deleted successfully');
            this.loadComments(this.currentDateFilters.date_from, this.currentDateFilters.date_to);
          },
          error: (error) => {
            console.log('error: ', error);
            modalError('Error', error.error?.message || 'Failed to delete comment.');
          }
        });
      }
    });
  }

  async deleteValue(index: number) {
    const dataDelete = this.datosArray.at(index).getRawValue();

    Swal.fire({
      title: 'Are you sure you want to delete this weekly report',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel'
    })
    .then((result) => {
      if (result.isConfirmed) {
        loadingModal('Data Delete');
        this.weeklyReportV2Service.delete(dataDelete.id).subscribe({
          next: (response) => {
            modalSuccess('Delete Weekly Report', response.message ?? 'Weekly report deleted successfully');
            this.refreshWeeklyReports();
          },
          error: (error) => {
            console.log('error: ', error);
            modalError('Error', error.error.message);
          }
        });
      }
    });
  }

  openCategoryRegeneration(): void {
    this.openWeeklyReportRegenerationDialog('category');
  }

  openPlanRegeneration(): void {
    this.openWeeklyReportRegenerationDialog('plan');
  }

  recalculateWeeklyReport(index: number): void {
    const weeklyReport = this.datosArray.at(index)?.getRawValue();

    if (!weeklyReport?.id) {
      this.snackBar.open('Weekly report not found', 'Close', {
        duration: 2500,
        horizontalPosition: 'end',
        verticalPosition: 'top',
        panelClass: ['error-snackbar'],
      });
      return;
    }

    if (this.rowRegenerationLoading.has(weeklyReport.id)) {
      return;
    }

    this.rowRegenerationLoading.add(weeklyReport.id);

    const payload: WeeklyReportsRegenerationRequest = {
      target_type: 'point',
      weekly_report_id: weeklyReport.id,
    };

    this.weeklyReportV2Service.regenerateWeeklyReports(payload).pipe(take(1)).subscribe({
      next: (response: WeeklyReportsRegenerationResponse) => {
        this.rowRegenerationLoading.delete(weeklyReport.id);
        this.snackBar.open(
          `Weekly report recalculated (${response.data?.summary?.updated ?? 0} updated)`,
          'Close',
          {
            duration: 10000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
            panelClass: ['success-snackbar'],
          }
        );
        this.refreshWeeklyReports();
      },
      error: (error) => {
        console.log('error: ', error);
        this.rowRegenerationLoading.delete(weeklyReport.id);
        this.snackBar.open(error?.error?.message || 'Failed to recalculate weekly report', 'Close', {
          duration: 10000,
          horizontalPosition: 'end',
          verticalPosition: 'top',
          panelClass: ['error-snackbar'],
        });
      },
    });
  }

  isWeeklyReportRecalculating(weeklyReportId: number): boolean {
    return this.rowRegenerationLoading.has(weeklyReportId);
  }

  get canShowRegenerationActions(): boolean {
    return this.isEditable && !this.isPdfGeneration;
  }

  get categoryRegenerationLabel(): string {
    return this.resolveCategoryLabel(this.maladaptive?.category);
  }

  private openWeeklyReportRegenerationDialog(targetType: 'category' | 'plan'): void {
    if (this.isRegeneratingWeeklyReports) {
      return;
    }

    const dialogData = this.buildWeeklyReportRegenerationDialogData(targetType);
    if (!dialogData) {
      return;
    }

    const dialogRef = this.dialog.open(WeeklyReportRegenerationDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result: WeeklyReportRegenerationDialogResult | undefined) => {
      if (!result) {
        return;
      }

      const payload = this.buildWeeklyReportsRegenerationPayload(targetType, result);
      if (!payload) {
        return;
      }

      this.executeWeeklyReportsRegeneration(payload);
    });
  }

  private buildWeeklyReportRegenerationDialogData(
    targetType: 'category' | 'plan'
  ): WeeklyReportRegenerationDialogData | null {
    const { defaultStartDate, defaultEndDate } = this.getDefaultRegenerationDateRange();

    if (targetType === 'category') {
      if (!this.maladaptive?.category) {
        return null;
      }

      return {
        targetType,
        title: `Regenerate ${this.categoryRegenerationLabel}`,
        scopeLabel: `${this.categoryRegenerationLabel.toLowerCase()} reports`,
        defaultStartDate,
        defaultEndDate,
        allowTeacherTraining: this.maladaptive.category === 'caregiver_training',
      };
    }

    return {
      targetType,
      title: 'Regenerate Plan Reports',
      scopeLabel: this.maladaptive?.name || 'this plan',
      defaultStartDate,
      defaultEndDate,
    };
  }

  private buildWeeklyReportsRegenerationPayload(
    targetType: 'category' | 'plan',
    result: WeeklyReportRegenerationDialogResult
  ): WeeklyReportsRegenerationRequest | null {
    if (targetType === 'category') {
      const bipId = this.bipId ?? this.maladaptive?.bip_id;
      const category = this.maladaptive?.category;

      if (!bipId || !category) {
        modalError('Error', 'BIP id and category are required to regenerate category reports.');
        return null;
      }

      return {
        target_type: 'category',
        bip_id: bipId,
        category,
        start_date: result.start_date,
        end_date: result.end_date,
        force: result.force,
        ...(result.include_teacher_training ? { include_teacher_training: true } : {}),
      };
    }

    if (!this.maladaptive?.id) {
      modalError('Error', 'Plan id is required to regenerate plan reports.');
      return null;
    }

    return {
      target_type: 'plan',
      plan_id: this.maladaptive.id,
      start_date: result.start_date,
      end_date: result.end_date,
      force: result.force,
    };
  }

  private executeWeeklyReportsRegeneration(payload: WeeklyReportsRegenerationRequest): void {
    this.isRegeneratingWeeklyReports = true;
    loadingModal('Regenerating weekly reports');

    this.weeklyReportV2Service.regenerateWeeklyReports(payload).pipe(take(1)).subscribe({
      next: (response) => {
        modalClose();
        modalSuccess('Weekly reports regenerated', this.formatWeeklyReportsRegenerationSummary(response));
        this.refreshWeeklyReports();
      },
      error: (error) => {
        console.log('error: ', error);
        modalClose();
        modalError('Error', this.extractRegenerationErrorMessage(error));
        this.isRegeneratingWeeklyReports = false;
      },
      complete: () => {
        this.isRegeneratingWeeklyReports = false;
      },
    });
  }

  private formatWeeklyReportsRegenerationSummary(response: WeeklyReportsRegenerationResponse): string {
    const summary = response.data?.summary;
    if (!summary) {
      return response.message || 'Weekly reports regenerated successfully';
    }

    return [
      `Created: ${summary.created}`,
      `Updated: ${summary.updated}`,
      `Unchanged: ${summary.unchanged}`,
    ].join(' | ');
  }

  private extractRegenerationErrorMessage(error: unknown): string {
    const httpError = error as {
      error?: {
        message?: string;
        errors?: Record<string, string[] | string>;
      };
      message?: string;
    };

    if (httpError?.error?.errors) {
      return Object.values(httpError.error.errors).flat().join('\n');
    }

    return httpError?.error?.message || httpError?.message || 'Failed to regenerate weekly reports';
  }

  private getDefaultRegenerationDateRange(): { defaultStartDate: string | null; defaultEndDate: string | null } {
    if (this.currentDateFilters.date_from || this.currentDateFilters.date_to) {
      return {
        defaultStartDate: this.currentDateFilters.date_from ?? null,
        defaultEndDate: this.currentDateFilters.date_to ?? null,
      };
    }

    const weekStarts = (this.dataGraphic || [])
      .map((report) => this.normalizeDateValue(report.week_start))
      .filter((value) => !!value);
    const weekEnds = (this.dataGraphic || [])
      .map((report) => this.normalizeDateValue(report.week_end))
      .filter((value) => !!value);

    return {
      defaultStartDate: weekStarts[0] || null,
      defaultEndDate: weekEnds.length > 0 ? weekEnds[weekEnds.length - 1] : null,
    };
  }

  private refreshWeeklyReports(): void {
    this.getValues(this.currentDateFilters.date_from ?? null, this.currentDateFilters.date_to ?? null, true);
  }

  private resolveCategoryLabel(category?: string | null): string {
    switch (category) {
      case 'maladaptive':
        return 'Maladaptives';
      case 'replacement':
        return 'Replacements';
      case 'caregiver_training':
        return 'Caregiver Training';
      case 'rbt_training':
        return 'RBT Training';
      case 'teacher_training':
        return 'Teacher Training';
      default:
        return 'Weekly Reports';
    }
  }

  private normalizeCommentTextPosition(position: unknown): ChartCommentTextPosition {
    if (position === 'middle' || position === 'bottom') {
      return position;
    }

    return 'top';
  }

  get datosArray() {
    return this.formTableGraphic.get('datos') as FormArray;
  }

  get commentsArray() {
    return this.formCommentsTable.get('comments') as FormArray;
  }

  get commentDateControl() {
    return this.formAddComment.get('comment_date') as FormControl;
  }

  get commentTextControl() {
    return this.formAddComment.get('comment') as FormControl;
  }

  get week_end() {
    return this.formAddWeeklyReport.get('week_end') as FormControl;
  }

  get week_start() {
    return this.formAddWeeklyReport.get('week_start') as FormControl;
  }

  // Helper method to create timezone-safe dates
  private createSafeDate(dateInput: Date | string): Date {
    if (dateInput instanceof Date) {
      return dateInput;
    }

    // If it's a string in YYYY-MM-DD format, create a local date
    if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateInput)) {
      const normalized = formatDateToYmd(dateInput) ?? dateInput;
      const [year, month, day] = normalized.split('-').map(Number);
      return new Date(year, month - 1, day); // month is 0-indexed
    }

    // Otherwise, use regular Date constructor
    return new Date(dateInput);
  }

  /**
   * Generate all Saturdays between start and end dates (inclusive)
   * @param startDate - The first date in the range
   * @param endDate - The last date in the range
   * @returns Array of Saturday dates
   */
  private generateSaturdaySequence(startDate: Date, endDate: Date): Date[] {
    const saturdays: Date[] = [];
    const current = new Date(startDate);

    // Find the first Saturday on or after startDate
    while (current.getDay() !== 6 && current <= endDate) {
      current.setDate(current.getDate() + 1);
    }

    // Generate all Saturdays until endDate
    while (current <= endDate) {
      saturdays.push(new Date(current));
      current.setDate(current.getDate() + 7); // Move to next Saturday
    }

    return saturdays;
  }

  handleImagesGenerated(images: string[]) {
    const maxImages = 160;

    if (SaturdayChartComponent.allChartImages.length >= maxImages) {
      return;
    }
    SaturdayChartComponent.allChartImages = [...SaturdayChartComponent.allChartImages, ...images];
    this.imagesGenerated.emit(SaturdayChartComponent.allChartImages);
  }

  async captureChartImage(): Promise<string[]> {

    if (!this.chart) {
      console.warn(`❌ Chart component not available for goal: ${this.maladaptive?.name || 'Unknown'}`);
      return [];
    }

    if (!this.maladaptive) {
      console.warn('❌ Maladaptive data not available for chart capture');
      return [];
    }

    try {
      // Use the custom line chart's SVG capture method
      const imageBase64 = await this.chart.captureChartAsSvg();
      if (imageBase64) {
        return [imageBase64];
      } else {
        console.warn(`⚠️ Empty image captured for ${this.maladaptive.name}`);
        return [];
      }
    } catch (error) {
      console.error(`❌ Error capturing chart image for ${this.maladaptive.name}:`, error);
      return [];
    }
  }

  async copyChartToClipboard(): Promise<void> {
    if (!this.chart || !this.maladaptive) {
      return;
    }

    try {
      this.isExporting = true;
      const imageBase64 = await this.chart.captureChartAsSvg();
      if (!imageBase64) {
        this.snackBar.open('Unable to copy chart image', 'Close', { duration: 3000 });
        return;
      }

      const blob = await fetch(imageBase64).then((response) => response.blob());
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      this.snackBar.open('Chart copied to clipboard', 'Close', { duration: 3000 });
    } catch (error) {
      console.error('Failed to copy chart image', error);
      const errorName = (error as DOMException | Error | undefined)?.name;
      const message =
        errorName === 'NotAllowedError'
          ? 'Operation not allowed by your browser\'s settings'
          : 'Failed to copy chart image';
      this.snackBar.open(message, 'Close', { duration: 3000 });
    } finally {
      this.isExporting = false;
    }
  }

  async downloadChartPng(): Promise<void> {
    if (!this.chart || !this.maladaptive) {
      return;
    }

    try {
      this.isExporting = true;
      const imageBase64 = await this.chart.captureChartAsSvg();
      if (!imageBase64) {
        this.snackBar.open('Unable to download chart image', 'Close', { duration: 3000 });
        return;
      }

      const fileName = this.getExportFileName();
      const link = document.createElement('a');
      link.href = imageBase64;
      link.download = fileName;
      link.click();
      link.remove();
      this.snackBar.open('Chart downloaded', 'Close', { duration: 3000 });
    } catch (error) {
      console.error('Failed to download chart image', error);
      this.snackBar.open('Failed to download chart image', 'Close', { duration: 3000 });
    } finally {
      this.isExporting = false;
    }
  }

  private getExportFileName(): string {
    const baseName = this.slugifyName(this.maladaptive?.name || 'chart');
    return `${baseName}.png`;
  }

  private slugifyName(value: string): string {
    return value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  }
}
